import { cn } from '@realtyfeed/utils';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { FOCUS_RING } from '../focusRing';

export type NavbarIconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  icon: ReactNode;
  /** Unread count. `0`/undefined hides the badge; anything over 99 shows `99+`. */
  badgeCount?: number;
};

/**
 * The navbar's round icon control — the comp's notification bell, and the
 * theme toggle rendered to match it.
 *
 * `forwardRef` + spread props so it can be a Radix `DropdownMenuTrigger asChild`
 * target, which is how the bell is wired.
 */
export const NavbarIconButton = forwardRef<HTMLButtonElement, NavbarIconButtonProps>(
  ({ icon, badgeCount, className, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn(
        'relative flex h-control w-control items-center justify-center rounded-full text-gdx-nav transition-colors hover:bg-gdx-indigo-tint hover:text-ds-text-accent dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white',
        FOCUS_RING,
        className
      )}
      {...props}
    >
      {icon}
      {!!badgeCount && badgeCount > 0 && (
        <span className="absolute end-1 top-1 min-w-[16px] rounded-md bg-gdx-danger px-1 text-[10px] font-bold leading-4 text-white">
          {badgeCount > 99 ? '99+' : badgeCount}
        </span>
      )}
    </button>
  )
);

NavbarIconButton.displayName = 'NavbarIconButton';
